import React, { useEffect, Suspense, lazy } from 'react';
import { Link, Route, Switch, matchPath, withRouter } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';
import LibraryIcon from '@mui/icons-material/AutoStories';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReportsIcon from '@mui/icons-material/Assignment';
import DataIcon from '@mui/icons-material/InsertChart';
import ListIcon from '@mui/icons-material/ViewList';
import RoomIcon from '@mui/icons-material/Room';
import PhotosIcon from '@mui/icons-material/PhotoLibrary';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PageTitle from 'global/components/PageTitle';
import NavToTop from 'feature/nav/NavToTop';
import ReportsFilters from 'feature/report/Filters';
import TasksFilters from 'feature/task/Filters';
import TabContainer from 'global/components/TabContainer';
import { TasksIcon } from 'global/components/PerformIcons';
import DocumentList from 'feature/document/DocumentList';
import { EditionAccessProvider } from 'feature/document/EditionAccess';
import EditionList from 'feature/document/EditionList';
import TaskList from 'feature/task/TaskList/TaskList';
import Document from 'feature/document/Document';
import ReportList from 'feature/report/ReportList';
import ReportData from 'feature/report/ReportData';
import ReportPhotos from 'feature/report/ReportPhotos';
import { TaskListOptions, TaskListSCOptions, ReportListOptions } from 'global/lib/sort';
import { ScreenCenterContent, LeftSideBar, RightSideBar, GridContainer } from 'global/components/perform-grid';
import { useVendors } from 'global/hooks/useVendors';
import PermissionGate from 'global/components/permission-gate';
import { CenterLoader, useScreen } from 'global/styles';
import { CourseList } from '../../document/CourseList';
import { CourseEditionList } from '../../document/CourseEditionList';
import { CourseDocument } from '../../document/CourseDocument';
import TaskListSC from '../../task/TaskListSC';
import { useHashView } from 'feature/share/useHashView';
import FlexibleLayout from 'global/components/FlexibleLayout';
import { DocumentSoloShell, DocumentSplitShell } from 'global/components/FlexibleLayout/styles';
import { isNativeShell } from 'global/utils/nativeShell';

const AgentPage = lazy(() => import(/* webpackChunkName: "agent" */ '../../agent'));

export const PerformEditionPage = PermissionGate((props) => {
  useEffect(() => {
    props.changeTitle(<PageTitle Icon={LibraryIcon} title="Library" />);
  }, []);

  return <PerformEditionList {...props} />;
});

export const PerformCoursesPage = PermissionGate((props) => {
  useEffect(() => {
    props.changeTitle(<PageTitle Icon={MenuBookIcon} title="Courses" />);
  }, []);

  return <PerformCoursesList {...props} />;
});

export const PerformAgentPage = PermissionGate((props) => {
  useEffect(() => {
    props.changeTitle(<PageTitle Icon={AutoAwesomeIcon} title="Agent" />);
  }, []);

  return (
    <Suspense fallback={<CenterLoader />}>
      <AgentPage />
    </Suspense>
  );
});

export const PerformCourseEditionsPage = PermissionGate((props) => {
  const sidebar = matchPath(props.location.pathname, { path: '/courses/:id/document/:document', exact: true, strict: true });
  const isDocumentPath = sidebar != null;

  useEffect(() => {
    props.changeTitle(<PageTitle Icon={MenuBookIcon} title="Courses" />);
  }, []);

  return (
    <DocumentSplitShell>
      <FlexibleLayout
        variant="sidebar"
        fullWidthContent
        showRightPanel={isDocumentPath}
        leftPanel={<PerformCourseEditionList isDocumentPath={isDocumentPath} />}
        rightPanel={<Route path="/courses/:id/document/:document" component={CourseDocument} />}
      />
    </DocumentSplitShell>
  );
});

export const PerformLibraryPage = PermissionGate((props) => {
  const sidebar = matchPath(props.location.pathname, { path: '/library/:library/document/:document', exact: true, strict: true });
  // PFM-2641: a hashed share-link viewer is pinned to the single shared document
  // (HashViewGuard), so the edition's document-list sidebar must not render —
  // the ticket asks for "the specific document page, with the sidebar hidden".
  // This branch also must not mount EditionAccessProvider: GET /editions/:id?include=realm
  // is not served under /sharehash/v1. The hashed document reads the edition through
  // edition-documents/:id?include=edition instead. Skipping DocumentList also skips
  // its list/FilterChips reads (/edition-documents, /client-vendors, /supersets).
  const { active: hashViewActive } = useHashView();
  const isDocumentPath = sidebar != null;

  useEffect(() => {
    props.changeTitle(<PageTitle Icon={LibraryIcon} title="Library" />);
  }, []);

  // Solo shell: same viewport height as split; Document is the scrollport
  // (title + tabs + body scroll together). Sidebar stays suppressed.
  if (hashViewActive) {
    return (
      <DocumentSoloShell>
        <Route path="/library/:library/document/:document" component={Document} />
      </DocumentSoloShell>
    );
  }

  // Split shell: left list stays mounted and animates width; right document grows in.
  // EditionAccessProvider owns GET /editions/:id?include=realm for both panels.
  return (
    <EditionAccessProvider editionId={props.match.params.library}>
      <DocumentSplitShell>
        <FlexibleLayout
          variant="sidebar"
          fullWidthContent
          showRightPanel={isDocumentPath}
          leftPanel={<PerformLibraryList isDocumentPath={isDocumentPath} />}
          rightPanel={<Route path="/library/:library/document/:document" component={Document} />}
        />
      </DocumentSplitShell>
    </EditionAccessProvider>
  );
});

export const PerformReportsPage = PermissionGate((props) => {
  const vendors = useVendors(undefined, { includeUsersRealm: true, include: 'vendor', includeBuilders: true });

  const { isMobile } = useScreen();
  const { isUp } = useScreen('xl')

  useEffect(() => {
    props.changeTitle(<PageTitle Icon={ReportsIcon} title="Assessments" />);
  }, []);

  useEffect(() => {
    if (props.location.pathname === '/reports') {
      props.history.replace(`reports/list`);
    }
  }, [props.location]);

  const isData = props.match.params.display == 'data';
  const isPhoto = props.match.params.display == 'photo';
  const isWideContent = isData || isPhoto;

  return (
    <GridContainer>
      <LeftSideBar hidden={isMobile} boxProps={{ style: { zIndex: 1000 } }}>
        <ReportsFilters {...props.match.params} vendors={vendors} />
      </LeftSideBar>

      <ScreenCenterContent ignoreRight={isWideContent}>
        <NavToTop />
        <Tabs
          value={props.match.params.display || 'list'}
          centered
          indicatorColor="secondary"
          textColor="secondary"
          style={isWideContent && isUp ? {marginRight: 'calc(100% / 3)'} : undefined}
          TabIndicatorProps={isNativeShell() ? { style: { transition: 'none' } } : undefined}
        >
          <Tab component={Link} to={`${'list'}`} icon={<ListIcon />} label={'Browse'} value={'list'} key={0} />
          <Tab component={Link} to={`${'data'}`} icon={<DataIcon />} label={'Data'} value={'data'} key={1} />
          <Tab component={Link} to={`${'photo'}`} icon={<PhotosIcon />} label={'Photos'} value={'photo'} key={2} />
        </Tabs>
        <Switch>
          <Route
            exact
            path="/reports/list"
            render={(match) => (
              <TabContainer key={0}>
                <PerformReportsList sort={{ options: ReportListOptions, showNone: false }} vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/reports/data"
            render={(match) => (
              <TabContainer key={1}>
                <ReportData vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/reports/photo"
            render={(match) => (
              <TabContainer key={2}>
                <PerformReportsPhotos {...props.match.params} />
              </TabContainer>
            )}
          />
        </Switch>
      </ScreenCenterContent>

      <RightSideBar />
    </GridContainer>
  );
});

export const PerformTasksPage = PermissionGate((props) => {
  const vendors = useVendors(undefined, { includeUsersRealm: true, include: 'vendor' });

  useEffect(() => props.changeTitle(<PageTitle Icon={TasksIcon} title="Tasks" />), []);

  useEffect(() => {
    if (props.location.pathname === '/tasks') {
      props.history.replace(`tasks/list`);
    }
  }, [props.location]);

  const { isMobile } = useScreen();
  const { isUp } = useScreen('xl')
  const isListView = props.match.params.display === 'scorecard';

  return (
    <GridContainer>
      <LeftSideBar hidden={isMobile}>
        <TasksFilters {...props.match.params} vendors={vendors} />
      </LeftSideBar>
      <ScreenCenterContent ignoreRight={isListView}>
        <NavToTop />

        <Box style={{marginRight:(isListView && isUp ? "calc(100% / 3)" : undefined)}} sx={{
          displayPrint: "none"
        }}>
          <Tabs value={props.match.params.display || 'list'} centered indicatorColor="secondary" textColor="secondary">
            <Tab component={Link} to={`${'/tasks/list'}`} icon={<RoomIcon />} label={'By Location'} value={'list'} key={0} />
            <Tab component={Link} to={`${"/tasks/scorecard"}`} icon={<ListIcon />} label={"List"} value={"scorecard"} key={2}/>
          </Tabs>
        </Box>
        <Switch>
          <Route
            path="/tasks/list"
            render={(m) => (
              <TabContainer key={0}>
                <PerformTasksList vendors={vendors} sort={{ options: TaskListOptions }} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/tasks/scorecard"
            render={(m) => (
              <TabContainer key={2}>
                <PerformTaskListSC sort={{ options: TaskListSCOptions }} print={true} vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
        </Switch>
      </ScreenCenterContent>
      <RightSideBar />
    </GridContainer>
  );
});

const PerformCoursesList = withRouter(CourseList);
const PerformCourseEditionList = withRouter(CourseEditionList);
const PerformEditionList = withRouter(EditionList);
const PerformLibraryList = withRouter(DocumentList);
const PerformReportsList = withRouter(ReportList);
const PerformTasksList = withRouter(TaskList);
const PerformTaskListSC = withRouter(TaskListSC);
const PerformReportsPhotos = withRouter(ReportPhotos);
